import React from 'react';
import Loading from '../../components/Loading';

export interface Column<T> {
    key: string;
    header: string;
    render?: (item: T) => React.ReactNode;
    /** Texto usado na pesquisa. Se ausente, usa o valor bruto de `key`. */
    searchValue?: (item: T) => string;
}

interface DataTableProps<T> {
    data: T[];
    columns: Column<T>[];
    emptyMessage: string;
    isLoading?: boolean;
    /** Linhas por página. 0 desativa a paginação. */
    pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 50;

/** Atrasa a propagação do valor para não filtrar a cada tecla digitada. */
export function useDebouncedValue<T>(value: T, delay = 250) {
    const [debounced, setDebounced] = React.useState(value);

    React.useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);

    return debounced;
}

function toSearchText<T>(item: T, columns: Column<T>[]) {
    let text = '';

    for (const column of columns) {
        let value: unknown;

        if (column.searchValue) {
            value = column.searchValue(item);
        } else {
            const rendered = column.render ? column.render(item) : undefined;
            // Só aproveita o render quando ele devolve texto; JSX viraria "[object Object]".
            value =
                typeof rendered === 'string' || typeof rendered === 'number'
                    ? rendered
                    : (item as any)[column.key];
        }

        if (value !== null && value !== undefined) {
            text += String(value).toLowerCase() + ' ';
        }
    }

    return text;
}

export const DataTable = <T,>({
    data,
    columns,
    emptyMessage,
    isLoading = false,
    pageSize = DEFAULT_PAGE_SIZE,
}: DataTableProps<T>) => {
    const [searchInput, setSearchInput] = React.useState('');
    const [page, setPage] = React.useState(0);
    const searchTerm = useDebouncedValue(searchInput);

    // Índice de pesquisa calculado uma vez por conjunto de dados, e não a cada render.
    const searchIndex = React.useMemo(
        () => data.map(item => toSearchText(item, columns)),
        [data, columns]
    );

    const filteredData = React.useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return data;
        return data.filter((_, index) => searchIndex[index].includes(term));
    }, [data, searchIndex, searchTerm]);

    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(filteredData.length / pageSize)) : 1;
    const currentPage = Math.min(page, totalPages - 1);

    React.useEffect(() => {
        setPage(0);
    }, [searchTerm, data]);

    const visibleData = React.useMemo(() => {
        if (pageSize <= 0) return filteredData;
        const start = currentPage * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage, pageSize]);

    const firstRow = filteredData.length === 0 ? 0 : currentPage * pageSize + 1;
    const lastRow = Math.min(filteredData.length, (currentPage + 1) * pageSize);

    return (
        <div>
            <div className="_telastable-filter">
                <input
                    type="text"
                    placeholder="Pesquisar..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="_telasfilter-input"
                />
            </div>

            <table className="_telastable">
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th key={column.key}>{column.header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={columns.length}>
                                <Loading />
                            </td>
                        </tr>
                    ) : visibleData.length > 0 ? (
                        visibleData.map((item, index) => (
                            <tr key={currentPage * pageSize + index}>
                                {columns.map((column) => (
                                    <td key={column.key}>
                                        {column.render ? column.render(item) : (item as any)[column.key]}
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={columns.length}>
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {!isLoading && pageSize > 0 && filteredData.length > 0 && (
                <div className="_telastable-pagination">
                    <button
                        type="button"
                        onClick={() => setPage(currentPage - 1)}
                        disabled={currentPage === 0}
                    >
                        Anterior
                    </button>
                    <span className="_telastable-pagination-info">
                        {firstRow}-{lastRow} de {filteredData.length}
                    </span>
                    <button
                        type="button"
                        onClick={() => setPage(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1}
                    >
                        Próxima
                    </button>
                </div>
            )}
        </div>
    );
};
